import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
  Button,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import axios from "axios";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import config from "./global/config";

const InstallmentDetailsDialog = ({
  open,
  onClose,
  installmentId,
  colors,
  onUpdate,
}) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [rowModesModel, setRowModesModel] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);

  const createApiInstance = () => {
    return axios.create({
      // baseURL: "http://localhost/backend_commerciale",
      baseURL: config.apiUrl,

      withCredentials: true,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  };

  // In InstallmentDetailsDialog.jsx

  // In InstallmentDetailsDialog.jsx

  const handleDialogClose = async () => {
    try {
      if (details?.paymentDetails) {
        const api = createApiInstance();

        // Calculate if payment should be marked as complete
        const allPaid = details.installments.every(
          (inst) => inst.statut_reglements === "Payé" && inst.date_paiement
        );

        // Calculate total paid amount
        const totalPaid = details.installments.reduce(
          (sum, inst) =>
            inst.statut_reglements === "Payé"
              ? sum + Number(inst.montant)
              : sum,
          0
        );

        // Calculate net paid amount (considering refunds)
        const netPaidAmount = totalPaid - details.paymentDetails.totalRefunds;

        // Determine if payment is complete
        const isComplete =
          allPaid &&
          Math.abs(netPaidAmount - details.paymentDetails.montant_total) < 0.01;

        // Determine new status
        const newStatus = isComplete ? "Terminé" : "En cours";

        // Update status if it's different
        if (details.paymentDetails.statut_tranches !== newStatus) {
          const updateStatusFormData = new URLSearchParams();
          updateStatusFormData.append("METHOD", "PUT");
          updateStatusFormData.append("statut_tranches", newStatus);
          updateStatusFormData.append("user_upd", "SYSTEM");

          await api.post(
            `paiements_clients_tranches.php?id_paiement_tranche=${installmentId}`,
            updateStatusFormData
          );

          // Call onUpdate callback immediately after successful status update
          if (onUpdate) {
            onUpdate();
          }
        }
      }
    } catch (error) {
      //console.error("Error updating status on close:", error);
    } finally {
      // Call onClose handler
      if (onClose) {
        onClose();
      }

      // Reset state
      setDetails(null);
      setLoading(true);
      setError(null);
      setSuccessMessage("");
      setRowModesModel({});
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [installmentToDelete, setInstallmentToDelete] = useState(null);

  // Add delete handler
  const handleDeleteInstallment = async () => {
    if (!installmentToDelete) return;

    try {
      const api = createApiInstance();

      // Create form data for delete request
      const formData = new URLSearchParams();
      formData.append("METHOD", "DELETE");

      const response = await api.post(
        `reglements_tranches_clients.php?id_reglement=${installmentToDelete}`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      // Check if response contains error
      if (response.data.error) {
        throw new Error(response.data.error);
      }

      setSuccessMessage("Règlement supprimé avec succès");
      await fetchDetails(); // Refresh the data

      if (onUpdate) {
        onUpdate(); // Call the parent update callback
      }
    } catch (error) {
      //console.error("Delete error:", error);
      setError(error.message || "Erreur lors de la suppression du règlement");
    } finally {
      setDeleteDialogOpen(false);
      setInstallmentToDelete(null);
    }
  };

  const handleAddNewInstallment = () => {
    if (!details?.installments) return;

    const newInstallmentNumber = details.installments.length + 1;
    const lastInstallment =
      details.installments[details.installments.length - 1];
    const lastEcheanceDate = lastInstallment
      ? dayjs(lastInstallment.date_echeance)
      : dayjs();

    const nextEcheanceDate = details.paymentDetails.periodicite_jours
      ? lastEcheanceDate.add(
          Number(details.paymentDetails.periodicite_jours),
          "day"
        )
      : lastEcheanceDate.add(1, "month");

    const newInstallment = {
      id_reglement: `new-${Date.now()}`,
      id_paiement_tranche: installmentId,
      numero_tranche: newInstallmentNumber,
      date_echeance: nextEcheanceDate.format("YYYY-MM-DD"),
      montant: remainingAmount,
      statut_reglements: "En attente",
      mode_paiement: "",
      reference_paiement: "",
      commentaire: "",
    };

    setDetails((prev) => ({
      ...prev,
      installments: [...prev.installments, newInstallment],
    }));

    setRowModesModel((prev) => ({
      ...prev,
      [newInstallment.id_reglement]: { mode: "edit", isNew: true },
    }));
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: "edit" } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: "view" } });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: "view", ignoreModifications: true },
    });

    if (id.toString().startsWith("new-")) {
      setDetails((prev) => ({
        ...prev,
        installments: prev.installments.filter(
          (inst) => inst.id_reglement !== id
        ),
      }));
    }
  };

  const renderDatePicker = (params) => {
    const isInEditMode = rowModesModel[params.id]?.mode === "edit";
    const value = params.value ? dayjs(params.value) : null;

    if (isInEditMode) {
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={value}
            onChange={(newValue) => {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: newValue ? newValue.format("YYYY-MM-DD") : null,
              });
            }}
            clearable
            slotProps={{
              textField: {
                placeholder: "Non Date paiement",
              },
              actionBar: {
                actions: ["clear"],
              },
            }}
            sx={{
              "& .MuiInputBase-root": {
                color: colors.grey[100],
                bgcolor: colors.primary[900],
              },
              "& .MuiIconButton-root": {
                color: colors.grey[100],
              },
            }}
          />
        </LocalizationProvider>
      );
    }
    return formatDate(params.value);
  };

  const columns = [
    {
      field: "numero_tranche",
      headerName: "N° Tranche",
      flex: 1,
      editable: false,
    },
    {
      field: "date_echeance",
      headerName: "Date Échéance",
      flex: 1,
      editable: true,
      renderCell: renderDatePicker,
      renderEditCell: renderDatePicker,
    },
    {
      field: "montant",
      headerName: "Montant",
      flex: 1,
      editable: true,
      type: "number",
    },
    {
      field: "date_paiement",
      headerName: "Date Paiement",
      flex: 1,
      editable: true,
      renderCell: renderDatePicker,
      renderEditCell: renderDatePicker,
    },
    {
      field: "mode_paiement",
      headerName: "Mode Paiement",
      flex: 1,
      editable: true,
      type: "singleSelect",
      valueOptions: ["Espèces", "Chèque", "Virement", "Carte"],
    },
    {
      field: "reference_paiement",
      headerName: "Référence",
      flex: 1,
      editable: true,
    },
    {
      field: "statut_reglements",
      headerName: "Statut",
      flex: 1,
      editable: true,
      type: "singleSelect",
      valueOptions: ["En attente", "Payé"],
      renderCell: (params) => (
        <Chip
          label={params.value || "En attente"}
          size="small"
          sx={{
            bgcolor:
              params.value === "Payé"
                ? colors.greenAccent[600]
                : colors.blueAccent[700],
            color: colors.grey[100],
          }}
        />
      ),
    },
    {
      field: "commentaire",
      headerName: "Commentaire",
      flex: 1,
      editable: true,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 1,
      cellClassName: "actions",
      getActions: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === "edit";

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
              color="primary"
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleCancelClick(id)}
              color="error"
            />,
          ];
        }

        const actions = [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={handleEditClick(id)}
            color="primary"
          />,
        ];

        // Only allow deletion for existing records (not new ones)
        if (!id.toString().startsWith("new-")) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                setInstallmentToDelete(id);
                setDeleteDialogOpen(true);
              }}
              color="error"
            />
          );
        }

        return actions;
      },
    },
  ];

  const formatDate = (date) => {
    if (!date) return "Non Date paiement";
    try {
      return dayjs(date).format("DD/MM/YYYY");
    } catch (error) {
      //console.error("Date formatting error:", error);
      return "Non Date paiement";
    }
  };

  const validateTotalAmount = (newRow, allRows) => {
    const currentTotal = allRows.reduce((sum, row) => {
      if (row.id_reglement === newRow.id_reglement) {
        return sum + Number(newRow.montant);
      }
      return sum + Number(row.montant);
    }, 0);

    if (currentTotal > totalAmount) {
      throw new Error(
        `Le montant total des tranches (${currentTotal}) ne peut pas dépasser le montant total du paiement (${totalAmount})`
      );
    }

    return true;
  };

  const processRowUpdate = async (newRow) => {
    try {
      const api = createApiInstance();

      const allRows = details.installments.map((row) =>
        row.id_reglement === newRow.id_reglement ? newRow : row
      );
      validateTotalAmount(newRow, allRows);

      if (newRow.statut_reglements === "Payé" && !newRow.mode_paiement) {
        throw new Error(
          "Le mode de paiement est requis pour marquer comme payé"
        );
      }

      const requestData = new URLSearchParams();
      const isNewRow = newRow.id_reglement.toString().startsWith("new-");

      requestData.append("METHOD", isNewRow ? "POST" : "PUT");
      requestData.append("user_cre", "USER");
      requestData.append("user_upd", "USER");
      requestData.append("id_paiement_tranche", installmentId);

      requestData.append("numero_tranche", newRow.numero_tranche.toString());
      requestData.append("date_echeance", newRow.date_echeance);
      requestData.append("montant", newRow.montant.toString());
      requestData.append(
        "statut_reglements",
        newRow.statut_reglements || "En attente"
      );

      if (newRow.date_paiement) {
        requestData.append("date_paiement", newRow.date_paiement);
      }
      if (newRow.mode_paiement) {
        requestData.append("mode_paiement", newRow.mode_paiement);
      }
      if (newRow.reference_paiement) {
        requestData.append("reference_paiement", newRow.reference_paiement);
      }
      if (newRow.commentaire) {
        requestData.append("commentaire", newRow.commentaire);
      }

      const url = isNewRow
        ? "reglements_tranches_clients.php"
        : `reglements_tranches_clients.php?id_reglement=${newRow.id_reglement}`;

      //console.log("Request Data:", Object.fromEntries(requestData.entries()));
      const response = await api.post(url, requestData);

      if (
        typeof response.data === "string" &&
        response.data.includes("Error")
      ) {
        throw new Error(response.data);
      }

      setSuccessMessage("Modifications enregistrées avec succès");
      await fetchDetails();

      return newRow;
    } catch (error) {
      //console.error("Error processing row update:", error);
      setError(error.message || "Échec de l'enregistrement des modifications");
      throw error;
    }
  };

  // In InstallmentDetailsDialog.jsx, modify the fetchDetails function:

  const fetchDetails = async () => {
    if (!installmentId) return;
    //console.log("Starting fetchDetails with installmentId:", installmentId);

    setLoading(true);
    setError(null);

    try {
      const api = createApiInstance();
      //console.log("Fetching data from APIs...");

      // Fetch payment details, installments and refunds in parallel
      const [paymentResponse, installmentsResponse, refundsResponse] =
        await Promise.all([
          api.get(
            `paiements_clients_tranches.php?id_paiement_tranche=${installmentId}&details=true`
          ),
          api.get(
            `reglements_tranches_clients.php?id_paiement_tranche=${installmentId}`
          ),
          api.get(`refund_client.php?id_paiement_tranche=${installmentId}`),
        ]);

      const paymentData = paymentResponse.data[0] || paymentResponse.data;
      //console.log("Processed payment data:", paymentData);

      const installments =
        installmentsResponse.data?.map((inst) => ({
          ...inst,
          montant: Number(inst.montant || 0),
          date_echeance: inst.date_echeance
            ? dayjs(inst.date_echeance).format("YYYY-MM-DD")
            : null,
          date_paiement: inst.date_paiement
            ? dayjs(inst.date_paiement).format("YYYY-MM-DD")
            : null,
        })) || [];
      //console.log("Processed installments:", installments);

      const refunds = refundsResponse.data || [];
      //console.log("Processed refunds:", refunds);

      if (!paymentData) {
        throw new Error("Aucune donnée de paiement trouvée");
      }

      // Calculate total amount and remaining amount considering refunds
      const totalAmount = Number(paymentData.montant_total || 0);
      const totalRefunds = refunds.reduce(
        (sum, refund) =>
          refund.statut_refund === "Remboursé"
            ? sum + Number(refund.montant_refund)
            : sum,
        0
      );

      // Only count payments that are marked as paid and have a payment date
      const totalPaidAmount = installments.reduce(
        (sum, inst) =>
          inst.statut_reglements === "Payé" && inst.date_paiement
            ? sum + Number(inst.montant)
            : sum,
        0
      );
      const netPaidAmount = totalPaidAmount - totalRefunds;
      const remainingAmount = Math.max(0, totalAmount - netPaidAmount);

      // Calculate payment status
      let newStatus = "En attente";
      if (netPaidAmount > 0) {
        newStatus = "En cours";
        // Check if all installments are paid and amounts match
        const allInstallmentsPaid = installments.every(
          (inst) => inst.statut_reglements === "Payé" && inst.date_paiement
        );

        if (
          allInstallmentsPaid &&
          Math.abs(netPaidAmount - totalAmount) < 0.01
        ) {
          newStatus = "Terminé";
          //console.log("Setting status to Terminé - all conditions met");
        }
      }

      // Update payment status if it's different from current status
      if (newStatus !== paymentData.statut_tranches) {
        const updateStatusFormData = new URLSearchParams();
        updateStatusFormData.append("METHOD", "PUT");
        updateStatusFormData.append("statut_tranches", newStatus);
        updateStatusFormData.append("user_upd", "SYSTEM");

        try {
          const updateResponse = await api.post(
            `paiements_clients_tranches.php?id_paiement_tranche=${installmentId}`,
            updateStatusFormData
          );
          //console.log("Status update response:", updateResponse.data);
          paymentData.statut_tranches = newStatus;
        } catch (updateError) {
          //console.error("Error updating status:", updateError);
          //console.error("Error response:", updateError.response?.data);
          throw updateError;
        }
      } else {
        console.log(
          "No status update needed - current status is correct:",
          paymentData.statut_tranches
        );
      }

      setTotalAmount(totalAmount);
      setRemainingAmount(remainingAmount);
      setDetails({
        paymentDetails: {
          ...paymentData,
          totalRefunds,
          totalPaidAmount,
          netPaidAmount,
        },
        installments,
        refunds,
      });
    } catch (err) {
      setError(err.message || "Erreur lors du chargement des détails");
    } finally {
      setLoading(false);
      //console.log("fetchDetails completed");
    }
  };
  useEffect(() => {
    if (open && installmentId) {
      fetchDetails();
      setError(null);
      setSuccessMessage("");
    }
  }, [open, installmentId]);

  if (!open) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.primary[900],
            color: colors.grey[100],
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" component="div">
            Détails du Paiement en Tranches
          </Typography>
          <IconButton
            onClick={handleDialogClose}
            sx={{ color: colors.grey[100] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              p={3}
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : successMessage ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          ) : null}

          {details?.paymentDetails && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Informations du Paiement
              </Typography>
              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Montant Total:{" "}
                    {totalAmount.toLocaleString("fr-FR", {
                      style: "currency",
                      currency: "MAD",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Total Remboursements:{" "}
                    {details.paymentDetails.totalRefunds.toLocaleString(
                      "fr-FR",
                      {
                        style: "currency",
                        currency: "MAD",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Montant Net Payé:{" "}
                    {details.paymentDetails.netPaidAmount.toLocaleString(
                      "fr-FR",
                      {
                        style: "currency",
                        currency: "MAD",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}
                  </Typography>
                  <Typography variant="subtitle1">
                    Montant Restant:{" "}
                    {remainingAmount.toLocaleString("fr-FR", {
                      style: "currency",
                      currency: "MAD",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Statut:{" "}
                    <Chip
                      label={
                        details.paymentDetails.statut_tranches || "En cours"
                      }
                      size="small"
                      sx={{
                        bgcolor:
                          details.paymentDetails.statut_tranches === "Terminé"
                            ? colors.greenAccent[600]
                            : details.paymentDetails.statut_tranches ===
                                "En attente"
                              ? colors.blueAccent[700]
                              : colors.blueAccent[600],
                        color: colors.grey[100],
                        ml: 1,
                      }}
                    />
                  </Typography>
                  <Typography variant="subtitle1">
                    N° Paiement: {details.paymentDetails.numero_paiement || "-"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {details?.installments && (
            <Box>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h5">Liste des Tranches</Typography>
                {remainingAmount > 0 &&
                  details.installments &&
                  details.installments.reduce(
                    (sum, inst) => sum + Number(inst.montant),
                    0
                  ) < totalAmount && (
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<AddIcon />}
                      onClick={handleAddNewInstallment}
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        "&:hover": {
                          backgroundColor: colors.blueAccent[800],
                        },
                      }}
                    >
                      Ajouter une Tranche
                    </Button>
                  )}
              </Box>

              {details.installments.length > 0 ? (
                <Box sx={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={details.installments}
                    columns={columns}
                    getRowId={(row) => row.id_reglement}
                    editMode="row"
                    rowModesModel={rowModesModel}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={(error) => setError(error.message)}
                    sx={{
                      "& .MuiDataGrid-root": { border: "none" },
                      "& .MuiDataGrid-cell": { borderBottom: "none" },
                      "& .name-column--cell": {
                        color: colors.greenAccent[300],
                      },
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[700],
                        borderBottom: "none",
                      },
                      "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.secondary,
                      },
                      "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[700],
                      },
                      "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.grey[100]} !important`,
                      },
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="h6" sx={{ mt: 2, textAlign: "center" }}>
                  Aucune tranche trouvée
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setInstallmentToDelete(null);
          setError(null); // Clear any existing errors
        }}
        onConfirm={handleDeleteInstallment}
        title="Confirmer la suppression"
        content="Êtes-vous sûr de vouloir supprimer ce règlement ? Cette action ne peut pas être annulée."
      />
    </>
  );
};

export default InstallmentDetailsDialog;
